/**
 * CivicPulse — Report a Problem wizard (< 1 minute target).
 *
 * Steps: Capture photo → Locate on map → Confirm AI category + details
 * → Submit → success card with permanent public_id.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera, MapPin, Sparkles, Check, Loader2, ArrowRight, ArrowLeft,
  AlertTriangle, Info, X, Users, ShieldCheck,
} from "lucide-react";
import CivicMap from "@/components/civic/CivicMap";
import { civic, photoUrl } from "./api";
import { toast } from "sonner";
import { civicPath } from "./basePath";
import { useCivicI18n } from "./useCivicI18n";

const STEPS = ["capture", "locate", "details", "done"];
const KERALA_CENTER = { lat: 10.0261, lng: 76.3125 };

export default function CivicReport() {
  const navigate = useNavigate();
  const { t } = useCivicI18n();
  const [step, setStep] = useState("capture");
  const [photo, setPhoto] = useState(null);       // { url, path, privacy }
  const [uploading, setUploading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [ai, setAi] = useState(null);             // { category, title, description, urgency, emergency }
  const [locPin, setLocPin] = useState(null);     // { lat, lng }
  const [locMeta, setLocMeta] = useState({ address: "", district: "", city: "" });
  const [form, setForm] = useState({
    category: "", title: "", description: "",
    reporter_name: "", reporter_contact: "", anonymous: false, language: "en",
  });
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [issue, setIssue] = useState(null);
  const [duplicates, setDuplicates] = useState([]);

  useEffect(() => { civic.categories().then((d) => setCategories(d.items || [])); }, []);

  // When user has a category + location on Step 3, silently look up
  // nearby duplicates so we can nudge them to "I am also affected".
  useEffect(() => {
    if (step !== "details" || !form.category || !locPin) { setDuplicates([]); return; }
    let cancelled = false;
    civic.duplicatesCheck(form.category, locPin.lat, locPin.lng)
      .then((r) => { if (!cancelled) setDuplicates(r.candidates || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [step, form.category, locPin]);

  // Try to auto-fetch GPS when entering the "locate" step.
  useEffect(() => {
    if (step !== "locate" || locPin || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocPin({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setLocPin(KERALA_CENTER),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  }, [step, locPin]);

  async function onFilePicked(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const up = await civic.uploadPhoto(file);
      setPhoto({ url: up.url, path: up.path, privacy: up.privacy || null });
      if (up.privacy?.regions_blurred > 0) {
        toast.success(
          `${up.privacy.regions_blurred} ${up.privacy.regions_blurred === 1 ? "region" : "regions"} auto-blurred for privacy`,
          { description: t("privacy.chip_hint", "Faces and license plates are automatically blurred so people and vehicles in your photo stay anonymous.") }
        );
      }
      // Kick off AI suggestion in parallel — non-blocking.
      setSuggesting(true);
      civic.aiSuggest(up.url).then((r) => {
        if (r.ok && r.suggestion) {
          setAi(r.suggestion);
          setForm((f) => ({
            ...f,
            category:    r.suggestion.category || f.category,
            title:       r.suggestion.title || f.title,
            description: r.suggestion.description || f.description,
          }));
          if (r.suggestion.emergency) {
            toast.warning(t("report.emergency_warning", "This may be an emergency — please dial 112 first."));
          }
        }
      }).catch(() => {}).finally(() => setSuggesting(false));
    } catch (err) {
      toast.error(err.message || "Photo upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function submit() {
    if (!form.category || !form.title || !locPin) {
      toast.error("Please choose a category, title, and pin the location.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        category: form.category,
        title: form.title.trim(),
        description: form.description.trim() || null,
        photo_urls: photo ? [photo.url] : [],
        location: {
          lat: locPin.lat, lng: locPin.lng,
          address: locMeta.address || null,
          country: "India", state: "Kerala",
          district: locMeta.district || null,
          city: locMeta.city || null,
        },
        reporter_name: form.anonymous ? null : form.reporter_name || null,
        reporter_contact: form.anonymous ? null : form.reporter_contact || null,
        anonymous: form.anonymous,
        urgency: ai?.urgency || "medium",
        language: form.language,
      };
      const created = await civic.createIssue(body);
      setIssue(created);
      setStep("done");
    } catch (err) {
      toast.error(err.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="civic-report-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[.22em] text-slate-500">CivicPulse</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900">{t("report.title", "Report a Problem")}</h1>
          </div>
          <StepDots current={step} />
        </div>

        <div className="rounded-2xl bg-white ring-1 ring-emerald-100 p-5 sm:p-7 shadow-sm">
          {step === "capture" && (
            <CaptureStep photo={photo} uploading={uploading} onFilePicked={onFilePicked}
                         onNext={() => setStep("locate")} t={t} />
          )}
          {step === "locate" && (
            <LocateStep locPin={locPin} setLocPin={(lat, lng) => setLocPin({ lat, lng })}
                        locMeta={locMeta} setLocMeta={setLocMeta}
                        onBack={() => setStep("capture")}
                        onNext={() => setStep("details")} t={t} />
          )}
          {step === "details" && (
            <DetailsStep form={form} setForm={setForm} categories={categories}
                         ai={ai} suggesting={suggesting}
                         duplicates={duplicates}
                         onBack={() => setStep("locate")}
                         onSubmit={submit} submitting={submitting} t={t} />
          )}
          {step === "done" && issue && <DoneStep issue={issue} navigate={navigate} photo={photo} t={t} />}
        </div>

        <p className="text-xs text-slate-500 mt-6 text-center">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1 text-amber-600" />
          {t("report.not_emergency", "CivicPulse is not an emergency service. In an emergency, dial 112.")}
        </p>
      </div>
    </div>
  );
}

function StepDots({ current }) {
  return (
    <div className="hidden sm:flex items-center gap-2" data-testid="civic-report-steps">
      {STEPS.slice(0, 3).map((s, i) => {
        const done = STEPS.indexOf(current) > i;
        const active = current === s;
        return (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-8 h-8 grid place-items-center rounded-full text-xs font-bold
              ${done ? "bg-emerald-700 text-white" : active ? "bg-emerald-100 text-emerald-800 ring-2 ring-emerald-500" : "bg-slate-100 text-slate-500"}`}>
              {done ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            {i < 2 && <div className={`w-8 h-px ${done ? "bg-emerald-500" : "bg-slate-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function CaptureStep({ photo, uploading, onFilePicked, onNext, t }) {
  return (
    <div>
      <h2 className="font-serif text-2xl font-bold text-slate-900 mb-2">{t("report.step1.title", "Step 1 — Capture the problem")}</h2>
      <p className="text-slate-600 mb-6 text-sm">
        {t("report.step1.lead", "Snap a photo or upload one from your gallery. Our AI Civic Assistant will try to identify the issue automatically — you can still edit everything.")}
      </p>
      {!photo ? (
        <label
          data-testid="civic-photo-picker"
          className="block cursor-pointer rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50/40 py-14 text-center hover:bg-emerald-50 transition"
        >
          <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only"
                 onChange={onFilePicked} data-testid="civic-photo-input" />
          {uploading ? (
            <><Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-emerald-700" />
              <div className="font-semibold text-emerald-800">{t("report.step1.uploading", "Uploading…")}</div></>
          ) : (
            <>
              <Camera className="w-10 h-10 mx-auto mb-3 text-emerald-700" />
              <div className="font-semibold text-emerald-800">{t("report.step1.picker", "Take or choose a photo")}</div>
              <div className="text-xs text-slate-500 mt-1">{t("report.step1.picker_hint", "JPEG, PNG, or WebP up to 8 MB")}</div>
            </>
          )}
        </label>
      ) : (
        <div className="space-y-3">
          <div className="relative rounded-2xl overflow-hidden ring-1 ring-emerald-100 bg-slate-100">
            <img src={photoUrl(photo.url)} alt="Reported problem"
                 className="w-full max-h-96 object-cover" data-testid="civic-photo-preview" />
            {photo.privacy?.regions_blurred > 0 && (
              <BlurChip count={photo.privacy.regions_blurred} t={t} />
            )}
          </div>
          {photo.privacy?.regions_blurred > 0 && (
            <p className="text-xs text-slate-500 flex items-start gap-1.5" data-testid="civic-privacy-hint">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 shrink-0 mt-0.5" />
              {t("privacy.chip_hint", "Faces and license plates are automatically blurred so people and vehicles in your photo stay anonymous.")}
            </p>
          )}
        </div>
      )}
      <div className="mt-6 flex justify-end">
        <button disabled={!photo}
                onClick={onNext}
                data-testid="civic-report-next-1"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white font-semibold px-5 py-2.5 transition">
          {t("report.step1.next", "Next: locate on map")} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function BlurChip({ count, t }) {
  const label = count === 1
    ? t("privacy.regions_blurred", "regions blurred for privacy").replace(/s$/, "")
    : t("privacy.regions_blurred", "regions blurred for privacy");
  return (
    <div
      className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-900/85 backdrop-blur px-3 py-1.5 text-white text-xs font-semibold shadow-lg ring-1 ring-white/20"
      data-testid="civic-blur-chip"
    >
      <ShieldCheck className="w-3.5 h-3.5" />
      <span data-testid="civic-blur-chip-count">{count}</span>
      <span>{label}</span>
    </div>
  );
}

function LocateStep({ locPin, setLocPin, locMeta, setLocMeta, onBack, onNext, t }) {
  return (
    <div>
      <h2 className="font-serif text-2xl font-bold text-slate-900 mb-2">{t("report.step2.title", "Step 2 — Pin the location")}</h2>
      <p className="text-slate-600 mb-4 text-sm">
        {t("report.step2.lead", "We tried to auto-detect where you are. Tap or drag the marker to move it. This helps the responsible authority find the exact spot.")}
      </p>
      <CivicMap
        issues={[]}
        height={340}
        interactive
        center={locPin ? [locPin.lat, locPin.lng] : [KERALA_CENTER.lat, KERALA_CENTER.lng]}
        zoom={locPin ? 15 : 8}
        onPickLocation={setLocPin}
        pickedLocation={locPin}
        fitToIssues={false}
      />
      <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm">
        <TextField label={t("report.step2.landmark", "Landmark / address")} value={locMeta.address}
                   onChange={(v) => setLocMeta({ ...locMeta, address: v })}
                   placeholder="e.g. Near KSRTC bus stand" testid="civic-loc-address" />
        <TextField label={t("report.step2.district", "District")} value={locMeta.district}
                   onChange={(v) => setLocMeta({ ...locMeta, district: v })}
                   placeholder="Kottayam" testid="civic-loc-district" />
        <TextField label={t("report.step2.city", "City / Village")} value={locMeta.city}
                   onChange={(v) => setLocMeta({ ...locMeta, city: v })}
                   placeholder="Kadanad" testid="civic-loc-city" />
      </div>
      {locPin && (
        <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-emerald-700" />
          {t("report.step2.pinned", "Pinned at")} {locPin.lat.toFixed(5)}, {locPin.lng.toFixed(5)}
        </p>
      )}
      <div className="mt-6 flex justify-between">
        <button onClick={onBack}
                data-testid="civic-report-back-2"
                className="inline-flex items-center gap-2 rounded-full text-slate-700 hover:bg-slate-100 font-medium px-4 py-2.5 transition">
          <ArrowLeft className="w-4 h-4" /> {t("report.step2.back", "Back")}
        </button>
        <button disabled={!locPin}
                onClick={onNext}
                data-testid="civic-report-next-2"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white font-semibold px-5 py-2.5 transition">
          {t("report.step2.next", "Next: describe the issue")} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function DetailsStep({ form, setForm, categories, ai, suggesting, duplicates = [], onBack, onSubmit, submitting, t }) {
  return (
    <div>
      <h2 className="font-serif text-2xl font-bold text-slate-900 mb-2">{t("report.step3.title", "Step 3 — Describe & confirm")}</h2>
      <p className="text-slate-600 mb-4 text-sm">
        {suggesting
          ? <span className="inline-flex items-center gap-1.5 text-emerald-800"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("report.step3.ai_working", "AI Civic Assistant is analysing your photo…")}</span>
          : ai
            ? <span className="inline-flex items-center gap-1.5 text-emerald-800"><Sparkles className="w-3.5 h-3.5" /> {t("report.step3.ai_done", "Suggested by AI — review and edit anything below.")}</span>
            : t("report.step3.fill_details", "Fill in the details below.")}
      </p>
      {duplicates.length > 0 && (
        <div className="rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-4 mb-5" data-testid="civic-duplicates-banner">
          <div className="font-semibold text-amber-900 flex items-center gap-1.5 text-sm">
            <Users className="w-4 h-4" />
            {duplicates.length} {t("report.step3.duplicates", "similar reports nearby")}
          </div>
          <p className="text-xs text-amber-800 mt-1">
            {t("report.step3.duplicates_hint", "Instead of creating a duplicate, consider marking yourself as also affected on an existing one — it makes the issue harder to ignore.")}
          </p>
          <ul className="mt-3 space-y-1.5 text-sm">
            {duplicates.slice(0, 3).map((d) => (
              <li key={d.public_id}>
                <a href={`${window.location.origin}${window.location.pathname.startsWith("/civic") ? "/civic" : ""}/issue/${d.public_id}`}
                   className="inline-flex items-center gap-2 text-amber-900 hover:underline">
                  <span className="font-mono text-[11px] px-1.5 py-0.5 bg-amber-100 rounded">{Math.round(d.distance_m)}m</span>
                  <span className="font-medium">{d.title}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid gap-4">
        <label className="block text-sm">
          <div className="text-slate-600 mb-1 font-medium">{t("report.step3.category", "Category")}</div>
          <select data-testid="civic-form-category" value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500">
            <option value="">{t("report.step3.category_placeholder", "Select a category…")}</option>
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label_en}</option>
            ))}
          </select>
        </label>
        <TextField label={t("report.step3.title_field", "Title")} value={form.title}
                   onChange={(v) => setForm({ ...form, title: v })}
                   placeholder="e.g. Large pothole on MG Road" testid="civic-form-title" />
        <label className="block text-sm">
          <div className="text-slate-600 mb-1 font-medium">{t("report.step3.description", "Description (optional)")}</div>
          <textarea rows={3} data-testid="civic-form-description" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder={t("report.step3.description_placeholder", "Add any detail that helps identify the problem.")} />
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label={t("report.step3.name", "Your name (optional)")} value={form.reporter_name}
                     disabled={form.anonymous}
                     onChange={(v) => setForm({ ...form, reporter_name: v })}
                     placeholder="Priya S." testid="civic-form-name" />
          <TextField label={t("report.step3.contact", "Contact — email or phone (optional)")} value={form.reporter_contact}
                     disabled={form.anonymous}
                     onChange={(v) => setForm({ ...form, reporter_contact: v })}
                     placeholder="priya@example.com" testid="civic-form-contact" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" data-testid="civic-form-anonymous"
                 checked={form.anonymous}
                 onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                 className="rounded border-slate-300 text-emerald-700 focus:ring-emerald-500" />
          {t("report.step3.anonymous", "Publish this report anonymously (your name will be hidden from the public page).")}
        </label>
      </div>
      <div className="mt-6 flex justify-between">
        <button onClick={onBack} data-testid="civic-report-back-3"
                className="inline-flex items-center gap-2 rounded-full text-slate-700 hover:bg-slate-100 font-medium px-4 py-2.5 transition">
          <ArrowLeft className="w-4 h-4" /> {t("report.step2.back", "Back")}
        </button>
        <button disabled={submitting || !form.category || !form.title}
                onClick={onSubmit}
                data-testid="civic-report-submit"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white font-semibold px-6 py-2.5 transition">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {submitting ? t("report.step3.submitting", "Submitting…") : t("report.step3.submit", "Submit report")}
        </button>
      </div>
    </div>
  );
}

function DoneStep({ issue, navigate, photo, t }) {
  return (
    <div className="text-center py-8">
      <div className="w-16 h-16 mx-auto grid place-items-center rounded-full bg-emerald-100 text-emerald-700 mb-4">
        <Check className="w-8 h-8" />
      </div>
      <h2 className="font-serif text-3xl font-bold text-slate-900 mb-2">{t("report.done.title", "Thank you — it's on the map.")}</h2>
      <p className="text-slate-600">{t("report.done.id_label", "Your permanent CivicPulse ID is")}</p>
      <div className="mt-2 mb-4 font-mono text-lg text-emerald-800 font-bold" data-testid="civic-done-id">
        {issue.public_id}
      </div>
      {photo?.privacy?.regions_blurred > 0 && (
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-3 py-1.5 mb-6" data-testid="civic-done-blur-chip">
          <ShieldCheck className="w-3.5 h-3.5" />
          {photo.privacy.regions_blurred} {t("privacy.regions_blurred", "regions blurred for privacy")}
        </div>
      )}
      <div className="flex flex-wrap gap-3 justify-center">
        <button onClick={() => navigate(civicPath(`/issue/${issue.public_id}`))}
                data-testid="civic-done-view"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-5 py-2.5 transition">
          {t("report.done.view_issue", "View your issue")} <ArrowRight className="w-4 h-4" />
        </button>
        <button onClick={() => navigate(civicPath("/map"))}
                className="inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-emerald-700 text-emerald-800 hover:bg-emerald-50 font-semibold px-5 py-2.5 transition">
          {t("report.done.open_map", "Open civic map")}
        </button>
      </div>
      <p className="text-xs text-slate-500 mt-6 max-w-md mx-auto">
        <Info className="w-3.5 h-3.5 inline mr-1" />
        {t("report.done.info", "We'll notify the appropriate authority. Follow the issue to receive updates. You can share the URL above so others can support it.")}
      </p>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testid, disabled }) {
  return (
    <label className="block text-sm">
      <div className="text-slate-600 mb-1 font-medium">{label}</div>
      <input type="text" data-testid={testid} value={value || ""} disabled={disabled}
             onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 disabled:bg-slate-50 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
    </label>
  );
}
